import React from 'react';
import {
  List as ListIcon,
  Table2,
  CalendarDays,
  Search,
  Target,
  Group as GroupIcon,
  SlidersHorizontal,
  RefreshCw,
} from 'lucide-react';

export type ViewMode = 'list' | 'table' | 'calendar' | 'feed';
export type GroupKey = 'status' | 'pinned' | 'none';

const GROUP_LABELS: Record<GroupKey, string> = {
  status: 'Status',
  pinned: 'Pinned',
  none: 'None',
};

interface ViewTabConfig {
  id: ViewMode;
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
}

const VIEW_TABS: ViewTabConfig[] = [
  { id: 'list', label: 'List', icon: ListIcon },
  { id: 'table', label: 'Table', icon: Table2 },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
  { id: 'feed', label: 'Feed', icon: Target },
];

export interface FilterBarProps {
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
  search: string;
  onSearchChange: (value: string) => void;
  status?: string;
  onStatusChange?: (value: string) => void;
  onRefresh?: () => void;
  showSearch?: boolean;
  showGroup?: boolean;
  showStatusFilter?: boolean;
  groupBy?: GroupKey;
  onGroupByChange?: (key: GroupKey) => void;
}

// Values map to the status groups in backend get_calendar_items, so they match
// the clickable summary cards (SummaryStrip) one-to-one.
const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'ready', label: 'Ready' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'published', label: 'Published' },
  { value: 'failed', label: 'Failed' },
];

export const FilterBar: React.FC<FilterBarProps> = ({
  view,
  onViewChange,
  search,
  onSearchChange,
  status = 'all',
  onStatusChange,
  onRefresh,
  showSearch = true,
  showGroup = true,
  showStatusFilter = true,
  groupBy = 'status',
}) => {
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!showSearch) return;
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showSearch]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 28px',
        borderBottom: '1px solid var(--color-warm-border)',
        background: 'var(--color-warm-bg)',
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          background: 'var(--color-paper)',
          border: '1px solid var(--color-warm-border)',
          borderRadius: 8,
          padding: 3,
          gap: 2,
        }}
        role="tablist"
        aria-label="Content planner view"
      >
        {VIEW_TABS.map((tab) => {
          const active = view === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onViewChange(tab.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 5,
                background: active ? 'var(--color-coral-light)' : 'transparent',
                color: active ? 'var(--color-coral-dark)' : 'var(--color-ink-2)',
                border: 'none',
                fontSize: 12,
                fontWeight: active ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              <Icon size={14} strokeWidth={1.75} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div style={{ flex: 1 }} />

      {showSearch && (
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            padding: '5px 10px',
            minWidth: 220,
            background: 'var(--color-paper)',
            border: '1px solid var(--color-warm-border)',
            borderRadius: 7,
          }}
        >
          <Search size={14} strokeWidth={1.75} color="var(--color-ink-3)" />
          <input
            ref={searchInputRef}
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search articles, keywords..."
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 12.5,
              color: 'var(--color-ink)',
            }}
          />
          <kbd
            className="mono"
            style={{
              fontSize: 10,
              color: 'var(--color-ink-3)',
              padding: '1px 5px',
              border: '1px solid var(--color-warm-border)',
              borderRadius: 3,
              background: 'var(--color-warm-bg)',
            }}
          >
            ⌘K
          </kbd>
        </label>
      )}

      {showGroup && (
        <button
          type="button"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 11px',
            borderRadius: 7,
            background: 'var(--color-paper)',
            border: '1px solid var(--color-warm-border)',
            color: 'var(--color-ink-2)',
            fontSize: 12,
            fontWeight: 550,
            cursor: 'pointer',
          }}
        >
          <GroupIcon size={14} strokeWidth={1.75} />
          Group
          <span
            style={{
              padding: '0 5px',
              borderRadius: 8,
              background: 'var(--color-coral-light)',
              color: 'var(--color-coral-dark)',
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            {GROUP_LABELS[groupBy]}
          </span>
        </button>
      )}

      {showStatusFilter && (
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 10px',
            borderRadius: 7,
            background: 'var(--color-paper)',
            border: '1px solid var(--color-warm-border)',
            color: 'var(--color-ink-2)',
            fontSize: 12,
            fontWeight: 550,
          }}
        >
          <SlidersHorizontal size={14} strokeWidth={1.75} />
          <select
            value={status}
            onChange={(e) => onStatusChange?.(e.target.value)}
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'var(--color-ink-2)',
              fontSize: 12,
              fontWeight: 550,
              cursor: 'pointer',
            }}
            aria-label="Filter by status"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      )}

      <button
        type="button"
        aria-label="Refresh"
        onClick={onRefresh}
        style={{
          display: 'grid',
          placeItems: 'center',
          width: 30,
          height: 30,
          borderRadius: 7,
          background: 'var(--color-paper)',
          border: '1px solid var(--color-warm-border)',
          color: 'var(--color-ink-2)',
          cursor: 'pointer',
        }}
      >
        <RefreshCw size={14} strokeWidth={1.75} />
      </button>
    </div>
  );
};

export default FilterBar;
